import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import PasswordField from '../../components/shell/PasswordField';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Envelope,
  Gear,
  TestTube,
  CheckCircle,
  Warning,
  PaperPlaneTilt,
  Key,
  At,
  Lightning,
  FloppyDisk,
  ArrowCounterClockwise,
  UserPlus
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BRAND_NAME } from '../../brand';

export default function SMTPConfig() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  const [config, setConfig] = useState({
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    api_key: '',
    use_tls: true,
    use_ssl: false,
    from_email: '',
    from_name: 'MyLMS',
    reply_to: ''
  });

  const [templates, setTemplates] = useState({
    welcome_subject: 'Welcome to {{tenant_name}}!',
    welcome_body: 'Hi {{first_name}},\n\nWelcome to {{tenant_name}}! Your account has been created successfully.\n\nYou can now log in at: {{login_url}}\n\nBest regards,\n{{tenant_name}} Team',
    password_reset_subject: 'Reset Your Password',
    password_reset_body: 'Hi {{first_name}},\n\nWe received a request to reset your password. Click the link below to set a new password:\n\n{{reset_url}}\n\nThis link will expire in 1 hour.\n\nIf you did not request this, please ignore this email.\n\nBest regards,\n{{tenant_name}} Team',
    instructor_invite_subject: 'You\'ve Been Invited as an Instructor',
    instructor_invite_body: 'Hi {{first_name}},\n\nYou have been invited to join {{tenant_name}} as an instructor.\n\nYour temporary credentials:\nEmail: {{email}}\nPassword: {{temp_password}}\n\nPlease log in at: {{login_url}}\n\nBest regards,\n{{tenant_name}} Team'
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/admin/smtp/config');
      if (res.data) {
        if (res.data.smtp) {
          setConfig(prev => ({
            ...prev,
            ...res.data.smtp,
            // Preserve api_key if it exists
            api_key: res.data.smtp.api_key || prev.api_key || ''
          }));
        }
        if (res.data.templates) setTemplates(prev => ({ ...prev, ...res.data.templates }));
      }
      setDirty(false);
    } catch (error) {
      console.log('No existing SMTP config');
    } finally {
      setLoading(false);
    }
  };

  // Field updaters: patch state and mark the form dirty
  const updateConfig = (patch) => {
    setConfig(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const updateTemplates = (patch) => {
    setTemplates(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/smtp/config', { smtp: config, templates });
      toast.success('SMTP configuration saved successfully');
      setDirty(false);
    } catch (error) {
      toast.error('Failed to save SMTP configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    setTestResult(null);
    await fetchConfig();
    toast.info('Configuration reloaded');
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/smtp/test', {
        email: testEmail,
        smtp: config
      });
      setTestResult({ success: true, message: res.data.message || 'Test email sent successfully!' });
      toast.success('Test email sent!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send test email';
      setTestResult({ success: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const canTest = !!testEmail && (!!config.api_key || (!!config.host && !!config.username));

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Email (SMTP)" loading onBack={null} data-testid="smtp-config-page" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="smtp-config-page"
        title="Email (SMTP)"
        subtitle="Outgoing mail via SMTP or Brevo API"
        badge={<StatusBadge value={config.enabled ? 'enabled' : 'disabled'} />}
        onBack={null}
        onRefresh={fetchConfig}
        refreshing={loading}
        dirty={dirty}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : 'Save', icon: FloppyDisk, onClick: handleSave, disabled: saving, testid: 'save-smtp-btn' },
          { key: 'test', label: testing ? 'Sending…' : 'Send test email', icon: PaperPlaneTilt, variant: 'outline', onClick: handleTest, disabled: testing || !canTest, testid: 'send-test-btn', title: canTest ? 'Send a test email to the address in “Test connection”' : 'Enter a test email address (SMTP Settings → Test connection) first' },
        ]}
        secondary={[
          { key: 'reload', label: 'Reload', icon: ArrowCounterClockwise, onClick: handleReload, disabled: saving || testing, title: 'Reload saved configuration (discards unsaved edits)' },
        ]}
      >
        <Tabs defaultValue="smtp" className="space-y-4">
          <TabsList>
            <TabsTrigger value="smtp" className="flex items-center gap-2">
              <Gear className="w-4 h-4" />
              SMTP Settings
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <PaperPlaneTilt className="w-4 h-4" />
              Email Templates
            </TabsTrigger>
          </TabsList>

          {/* SMTP Settings Tab */}
          <TabsContent value="smtp" className="space-y-4">
            {/* Enable/Disable */}
            <FormSection
              title="Email Service"
              icon={Envelope}
              description="Enable SMTP to send emails for password resets, invitations, and notifications"
              actions={
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => updateConfig({ enabled: checked })}
                  data-testid="smtp-enabled-switch"
                />
              }
              bodyClassName="px-4 py-3"
            >
              <p className="text-sm text-muted-foreground">
                {config.enabled
                  ? 'Email sending is enabled. Configure the server, sender and templates below, then Save.'
                  : 'Email sending is disabled. Turn on the switch to configure SMTP or an API key.'}
              </p>
            </FormSection>

            {config.enabled && (
              <>
                {/* Quick presets */}
                <FormSection title="Quick Setup Presets" icon={Lightning} description="Fill in host, port and TLS for a common provider">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateConfig({ host: 'smtp.gmail.com', port: 587, use_tls: true, use_ssl: false })}
                    >
                      Gmail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateConfig({ host: 'smtp.office365.com', port: 587, use_tls: true, use_ssl: false })}
                    >
                      Outlook/Office 365
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateConfig({ host: 'smtp.sendgrid.net', port: 587, use_tls: true, use_ssl: false })}
                    >
                      SendGrid
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateConfig({ host: 'email-smtp.us-east-1.amazonaws.com', port: 587, use_tls: true, use_ssl: false })}
                    >
                      AWS SES
                    </Button>
                  </div>
                </FormSection>

                {/* SMTP Server Settings */}
                <FormSection title="SMTP Server" icon={Gear} bodyClassName="p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="field">
                      <Label htmlFor="host">SMTP Host</Label>
                      <Input
                        id="host"
                        placeholder="smtp.gmail.com"
                        value={config.host}
                        onChange={(e) => updateConfig({ host: e.target.value })}
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="field">
                      <Label htmlFor="port">Port</Label>
                      <Input
                        id="port"
                        type="number"
                        placeholder="587"
                        value={config.port}
                        onChange={(e) => updateConfig({ port: parseInt(e.target.value) || 587 })}
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="field">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        placeholder="your-email@gmail.com"
                        value={config.username}
                        onChange={(e) => updateConfig({ username: e.target.value })}
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="field">
  <PasswordField id="password" label="Password / App Password" value={config.password} onChange={(v) => updateConfig({ password: v })} placeholder="••••••••" minLength={1} autoComplete="off" testid="smtp-password-input" />
                    </div>
                  </div>

                  {/* API Key - Recommended for Railway/Cloud hosting */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Recommended: Use API Key</h4>
                    <p className="text-sm text-blue-600 dark:text-blue-300 mb-3">
                      Cloud platforms like Railway often block SMTP ports. Use your email provider's API key instead for reliable delivery.
                    </p>
                    <div className="field">
  <PasswordField id="api_key" label="Brevo/SendGrid API Key" value={config.api_key || ''} onChange={(v) => updateConfig({ api_key: v })} placeholder="xkeysib-xxx... or SG.xxx..." minLength={1} autoComplete="off" testid="smtp-api-key-input" />
                      <p className="field-hint">
                        If API key is provided, emails will be sent via API instead of SMTP (bypasses port blocks)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="use_tls"
                        checked={config.use_tls}
                        onCheckedChange={(checked) => updateConfig({ use_tls: checked, use_ssl: checked ? false : config.use_ssl })}
                      />
                      <Label htmlFor="use_tls">Use TLS (Recommended)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="use_ssl"
                        checked={config.use_ssl}
                        onCheckedChange={(checked) => updateConfig({ use_ssl: checked, use_tls: checked ? false : config.use_tls })}
                      />
                      <Label htmlFor="use_ssl">Use SSL</Label>
                    </div>
                  </div>
                </FormSection>

                {/* Sender Settings */}
                <FormSection title="Sender Information" icon={At} bodyClassName="p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="field">
                      <Label htmlFor="from_email">From Email</Label>
                      <Input
                        id="from_email"
                        type="email"
                        placeholder="noreply@yourcompany.com"
                        value={config.from_email}
                        onChange={(e) => updateConfig({ from_email: e.target.value })}
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                    <div className="field">
                      <Label htmlFor="from_name">From Name</Label>
                      <Input
                        id="from_name"
                        placeholder={BRAND_NAME}
                        value={config.from_name}
                        onChange={(e) => updateConfig({ from_name: e.target.value })}
                        data-testid="smtp-from-name-input"
                      />
                    </div>
                  </div>

                  <div className="field">
                    <Label htmlFor="reply_to">Reply-To Email (Optional)</Label>
                    <Input
                      id="reply_to"
                      type="email"
                      placeholder="support@yourcompany.com"
                      value={config.reply_to}
                      onChange={(e) => updateConfig({ reply_to: e.target.value })}
                      data-testid="smtp-reply-to-input"
                    />
                  </div>
                </FormSection>

                {/* Test Email */}
                <FormSection title="Test Connection" icon={TestTube} description="Enter a recipient address, then use “Send test email” in the toolbar" bodyClassName="p-3 space-y-3">
                  <div className="field">
                    <Label htmlFor="test_email">Test recipient</Label>
                    <Input
                      id="test_email"
                      type="email"
                      placeholder="Enter email to send test"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="max-w-md"
                      data-testid="test-email-input"
                    />
                  </div>

                  {testResult && (
                    <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="test-result">
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5" weight="fill" />
                      ) : (
                        <Warning className="w-5 h-5" weight="fill" />
                      )}
                      {testResult.message}
                    </div>
                  )}
                </FormSection>
              </>
            )}
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            {/* Welcome Email */}
            <FormSection title="Welcome Email" icon={Envelope} description="Sent when a new user account is created" bodyClassName="p-3 space-y-3">
              <div className="field">
                <Label htmlFor="welcome_subject">Subject</Label>
                <Input
                  id="welcome_subject"
                  value={templates.welcome_subject}
                  onChange={(e) => updateTemplates({ welcome_subject: e.target.value })}
                  data-testid="welcome-subject-input"
                />
              </div>
              <div className="field">
                <Label htmlFor="welcome_body">Body</Label>
                <Textarea
                  id="welcome_body"
                  rows={6}
                  value={templates.welcome_body}
                  onChange={(e) => updateTemplates({ welcome_body: e.target.value })}
                  data-testid="welcome-body-input"
                />
              </div>
              <p className="field-hint">
                Available variables: {`{{first_name}}, {{last_name}}, {{email}}, {{tenant_name}}, {{login_url}}`}
              </p>
            </FormSection>

            {/* Password Reset Email */}
            <FormSection title="Password Reset Email" icon={Key} description="Sent when a user requests a password reset" bodyClassName="p-3 space-y-3">
              <div className="field">
                <Label htmlFor="password_reset_subject">Subject</Label>
                <Input
                  id="password_reset_subject"
                  value={templates.password_reset_subject}
                  onChange={(e) => updateTemplates({ password_reset_subject: e.target.value })}
                  data-testid="reset-subject-input"
                />
              </div>
              <div className="field">
                <Label htmlFor="password_reset_body">Body</Label>
                <Textarea
                  id="password_reset_body"
                  rows={6}
                  value={templates.password_reset_body}
                  onChange={(e) => updateTemplates({ password_reset_body: e.target.value })}
                  data-testid="reset-body-input"
                />
              </div>
              <p className="field-hint">
                Available variables: {`{{first_name}}, {{email}}, {{reset_url}}, {{tenant_name}}`}
              </p>
            </FormSection>

            {/* Instructor Invite Email */}
            <FormSection title="Instructor Invitation Email" icon={UserPlus} description="Sent when an instructor is invited to join" bodyClassName="p-3 space-y-3">
              <div className="field">
                <Label htmlFor="instructor_invite_subject">Subject</Label>
                <Input
                  id="instructor_invite_subject"
                  value={templates.instructor_invite_subject}
                  onChange={(e) => updateTemplates({ instructor_invite_subject: e.target.value })}
                  data-testid="invite-subject-input"
                />
              </div>
              <div className="field">
                <Label htmlFor="instructor_invite_body">Body</Label>
                <Textarea
                  id="instructor_invite_body"
                  rows={6}
                  value={templates.instructor_invite_body}
                  onChange={(e) => updateTemplates({ instructor_invite_body: e.target.value })}
                  data-testid="invite-body-input"
                />
              </div>
              <p className="field-hint">
                Available variables: {`{{first_name}}, {{email}}, {{temp_password}}, {{tenant_name}}, {{login_url}}`}
              </p>
            </FormSection>
          </TabsContent>
        </Tabs>
      </RecordPage>
    </DashboardLayout>
  );
}
